/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { promiseDispatch } from 'store/utils/promiseAction';

// Formik
import { Formik } from 'formik';

// Validation
import { companySchema } from 'yupSchemas';

// Material
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';

// Google
import ReCAPTCHA from 'react-google-recaptcha';

// Components
import { Button } from 'components';
import { NewAddressForm } from 'components/Forms/NewFormAddress';
import { MaskedInputCNPJ } from 'components/MaskedInputs';
import { PhoneMask } from 'components/PhoneMask';

// Actions
import * as CompaniesActions from 'store/ducks/companies/actions';
import { setOpen } from 'store/ducks/dialogs/actions';

// Constants
import environments from 'constants/enviroments';

// Modal Success
import { ModalSuccess } from './components';

// Styles
import useStyles from './formCompanyRegister.styles';

const FormCompanyRegister: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const companies = useSelector((state: any) => state.companies);

  const initialValues = useMemo(
    () => ({
      name: '',
      cnpj: '',
      phone: '',
      email: '',
      address: {
        cep: '',
        street: '',
        number: '',
        compl: '',
        neigh: '',
        city: '',
        state: '',
      },
      recaptcha: null,
    }),
    [],
  );

  const handleSubmit = async (values: any) => {
    const phone = `+55${values.phone.replace(/[^a-zA-Z0-9]/g, '')}`;
    const { status } = await promiseDispatch(
      CompaniesActions.createCompanyRequest({
        body: {
          name: values.name,
          cnpj: values.cnpj,
          phone,
          email: values.email,
          recaptcha: values.recaptcha,
          address: {
            cep: values.address.cep,
            street: values.address.street,
            number: values.address.number,
            compl: values.address.compl,
            neigh: values.address.neigh,
            city: values.address.city,
            state: values.address.state,
          },
        },
      }),
    );
    if (status && status === 200) {
      dispatch(setOpen({ open: true, content: <ModalSuccess /> }));
    }
  };

  return (
    <Box p='0.5rem'>
      <Box width='100%' display='flex' justifyContent='center' p='1rem'>
        <Typography style={{ fontSize: '1.5rem', fontWeight: 700 }}>
          Preencha com seus{' '}
          <span
            style={{ color: '#00CAD7', fontSize: '1.5rem', fontWeight: 700 }}>
            dados
          </span>
        </Typography>
      </Box>
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={companySchema}
        onSubmit={handleSubmit}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={classes.subtitle}>
                  Nome Completo
                </Typography>

                <TextField
                  className={classes.input}
                  name='name'
                  placeholder='Digite seu nome'
                  fullWidth
                  variant='outlined'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  error={Boolean(formik.errors.name && formik.touched.name)}
                  helperText={
                    formik.errors.name &&
                    formik.touched.name &&
                    formik.errors.name
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={classes.subtitle}>CNPJ</Typography>
                <TextField
                  className={classes.input}
                  name='cnpj'
                  placeholder='Digite seu cnpj'
                  fullWidth
                  variant='outlined'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.cnpj}
                  InputProps={{ inputComponent: MaskedInputCNPJ as any }}
                  error={Boolean(formik.errors.cnpj && formik.touched.cnpj)}
                  helperText={
                    formik.errors.cnpj &&
                    formik.touched.cnpj &&
                    formik.errors.cnpj
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={classes.subtitle}>Celular</Typography>
                <PhoneMask
                  formik={formik}
                  className={classes.inputPhone}
                  notfullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={classes.subtitle}>E-mail</Typography>
                <TextField
                  className={classes.input}
                  name='email'
                  placeholder='Digite seu e-mail'
                  fullWidth
                  variant='outlined'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  error={Boolean(formik.errors.email && formik.touched.email)}
                  helperText={
                    formik.errors.email &&
                    formik.touched.email &&
                    formik.errors.email
                  }
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography variant='h6'>Endereço</Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <NewAddressForm
                  fieldName='address'
                  spacing={3}
                  formik={formik}
                  className={classes.input}
                  notLabel
                  inputTitle
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <ReCAPTCHA
                  onChange={(value) => formik.setFieldValue('recaptcha', value)}
                  sitekey={`${environments.GOOGLE.RECAPTCHA_SITE_KEY}`}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              direction='row'
              className={classes.rowButton}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Button
                  color='primary'
                  showLoading={companies?.loading}
                  style={{ width: 200 }}
                  type='submit'
                  variant='contained'>
                  Criar
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default FormCompanyRegister;
export { FormCompanyRegister };
